'use client';

import { useState } from 'react';
import { Check, Copy, Mail } from 'lucide-react';
import type { GrantReport } from '@/lib/grants-embed/lookups';
import { shareEmail } from '@/lib/grants-embed/format';

/** Forward the report to whoever signs off on grants — usually the superintendent. */
export function SharePanel({ report, shareToken }: { report: GrantReport; shareToken: string }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/grants/report/${shareToken}`;
  const { subject, body } = shareEmail(report, url);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be denied; the link stays selectable below.
    }
  }

  return (
    <section className="rounded-[var(--fmxe-border-radius-md)] bg-white p-5 shadow-sm">
      <h3 className="g-display text-lg font-bold leading-snug">Send this to your superintendent</h3>
      <p className="mt-1 text-sm text-[var(--g-ink-soft)]">They get the same report: the map, the grants and the deadlines.</p>
      <input
        readOnly
        value={url}
        onFocus={(e) => e.currentTarget.select()}
        aria-label="Report link"
        className="mt-4 h-10 w-full rounded-[var(--fmxe-border-radius-sm)] border border-[var(--g-line)] bg-[var(--g-paper)] px-3 text-sm"
      />
      <div className="mt-2 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={copy}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-[var(--fmxe-border-radius-round)] border-2 border-[var(--g-ink)] px-3 text-sm font-bold"
        >
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />} {copied ? 'Copied' : 'Copy link'}
        </button>
        <a
          href={`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-[var(--fmxe-border-radius-round)] bg-[var(--g-ink)] px-3 text-sm font-bold text-white"
        >
          <Mail className="size-4" /> Email it
        </a>
      </div>
    </section>
  );
}
